<script lang="ts">
  /**
   * SharedDocument — Public share-link viewer (Spec 014 E8, S14, S15).
   *
   * Login-free route: /share/:token
   * This component is registered OUTSIDE the auth-guard block in App.svelte (S15).
   *
   * Behavior:
   * - Fetches GET /api/v1/share/{token} via api.sharing.getSharedDocument(token)
   * - 200 → displays SharedDocumentDto (title, category, download button)
   * - 401 → "Bitte einloggen" + login link (Authenticated-link, not logged in)
   * - 410 reason="expired" → i18n key pages.shared_document.error_expired
   * - 410 reason="revoked" → i18n key pages.shared_document.error_revoked
   * - 404 → i18n key pages.shared_document.error_invalid
   *
   * i18n keys required (Spec 014 E11):
   *   pages.shared_document.error_expired  DE: "Dieser Freigabe-Link ist abgelaufen."
   *   pages.shared_document.error_revoked  DE: "Dieser Freigabe-Link wurde widerrufen."
   *   pages.shared_document.error_invalid  DE: "Dieser Freigabe-Link ist ungültig."
   */
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { api, type SharedDocumentDto } from '../lib/api';
  import { authStore, startGoogleLogin } from '../lib/auth';
  import { toastStore } from '../lib/stores/toastStore';
  import Spinner from '../lib/components/ui/Spinner.svelte';

  // Route param — set by svelte-routing when the /share/:token route matches.
  export let token: string = '';

  // Component state
  // NOTE: no 'forbidden' state — PublicShareController never returns 403 (Spec 027 E1: the
  // Authenticated-link gate is IsAuthenticated only; whitelist membership is a display signal,
  // ViewerCanAccessFull, not an access gate).
  type LoadState = 'loading' | 'ok' | 'expired' | 'revoked' | 'invalid' | 'auth-required' | 'error';
  let state: LoadState = 'loading';
  let document: SharedDocumentDto | null = null;
  // Download-button state (Spec 054): downloading is now an async authenticated fetch
  // instead of an instant browser navigation, so it needs its own loading/error state.
  let downloading = false;

  onMount(async () => {
    try {
      document = await api.sharing.getSharedDocument(token);
      if (document.viewerCanAccessFull) {
        // Spec 027 E2/E3: a whitelisted member already has full app access — redirect to the
        // real detail page instead of showing the scoped share view. window.location.replace
        // (not svelte-routing's navigate) is deterministic and has no race with route
        // registration (spec note).
        window.location.replace(`/documents/${document.documentId}`);
        return;
      }
      state = 'ok';
    } catch (err: unknown) {
      const e = err as { status?: number; reason?: string };
      if (e.status === 410) {
        state = e.reason === 'revoked' ? 'revoked' : 'expired';
      } else if (e.status === 404) {
        state = 'invalid';
      } else if (e.status === 401) {
        state = 'auth-required';
      } else {
        state = 'error';
      }
    }
  });

  const errorView = {
    expired: { testid: 'share-error-expired', key: 'pages.shared_document.error_expired' },
    revoked: { testid: 'share-error-revoked', key: 'pages.shared_document.error_revoked' },
    invalid: { testid: 'share-error-invalid', key: 'pages.shared_document.error_invalid' },
    error: { testid: 'share-error-generic', key: 'pages.shared_document.error_generic' },
  } as const;

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  // Spec 054 (E6): resolves the signed content URL with an authenticated request, then
  // NAVIGATES the browser there instead of a plain <a href download> navigation (which never
  // carried the Authorization header) or a fetch()-into-Blob download (which would follow the
  // legacy /content redirect cross-origin and hit CORS — the document bucket has no CORS
  // config). Navigation ignores CORS, so this is the only path that works in a real browser.
  async function handleDownload() {
    if (!document || downloading) return;
    downloading = true;
    try {
      const url = await api.sharing.getSharedContentUrl(token);
      window.location.href = url;
    } catch {
      toastStore.error($_('pages.shared_document.download_error'));
    } finally {
      downloading = false;
    }
  }
</script>

{#if !$authStore}
  <!-- Spec 108 E6: logged-out recipients get no AppShell, so the brand row is the only sender hint -->
  <a
    href="/"
    data-testid="share-brand-link"
    aria-label={$_('pages.shared_document.brand_home_aria')}
    class="flex items-center justify-center gap-2 pt-6 text-text font-semibold no-underline"
  >
    <img src="/logo/logo-icon.svg" alt="" class="w-8 h-8" />
    <span>{$_('pages.login.title')}</span>
  </a>
{/if}

{#if state === 'loading'}
  <div class="flex justify-center py-12">
    <Spinner size="lg" label={$_('messages.loading')} />
  </div>
{:else if state === 'expired' || state === 'revoked' || state === 'invalid' || state === 'error'}
  <!-- S14: 410 expired/revoked, 404 invalid, other statuses (e.g. 5xx) generic -->
  <div class="max-w-xl mx-auto py-10 px-4 text-center">
    <p data-testid={errorView[state].testid}>{$_(errorView[state].key)}</p>
  </div>
{:else if state === 'auth-required'}
  <!-- S7: 401 (Authenticated-link, not logged in) -->
  <div class="flex flex-col items-center gap-4 py-12 px-4 text-center">
    <p data-testid="share-error-auth">{$_('pages.shared_document.error_auth_required')}</p>
    <button
      data-testid="share-login-button"
      class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-on-accent font-medium text-sm hover:bg-accent-hover transition-colors"
      on:click={startGoogleLogin}
    >
      {$_('pages.shared_document.login_link')}
    </button>
  </div>
{:else if state === 'ok' && document}
  <!-- Happy path: document display + download (Spec 014 E8, E10) -->
  <div class="max-w-xl mx-auto py-10 px-4" data-testid="share-document-view">
    <h1 class="text-xl font-semibold text-text mb-6" data-testid="share-document-title">
      {document.title}
    </h1>

    <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm mb-8">
      {#if document.category}
        <dt class="text-text-muted">{$_('pages.shared_document.category')}</dt>
        <dd class="text-text" data-testid="share-document-category">
          {document.category}{document.subCategory ? ` / ${document.subCategory}` : ''}
        </dd>
      {/if}
      {#if document.documentDate}
        <dt class="text-text-muted">{$_('pages.shared_document.document_date')}</dt>
        <dd class="text-text" data-testid="share-document-date">{document.documentDate}</dd>
      {:else}
        <dt class="text-text-muted">{$_('pages.shared_document.upload_date')}</dt>
        <dd class="text-text" data-testid="share-document-upload-date">
          {String(document.uploadDate).slice(0, 10)}
        </dd>
      {/if}
      <dt class="text-text-muted">{$_('pages.shared_document.file_name')}</dt>
      <dd class="text-text" data-testid="share-document-filename">{document.fileName}</dd>
      <dt class="text-text-muted">{$_('pages.shared_document.file_size')}</dt>
      <dd class="text-text" data-testid="share-document-filesize">
        {formatFileSize(document.fileSize)}
      </dd>
    </dl>

    <button
      type="button"
      data-testid="share-download-button"
      class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-on-accent font-medium text-sm hover:bg-accent-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      disabled={downloading}
      on:click={handleDownload}
    >
      {#if downloading}
        <Spinner size="sm" />
        {$_('pages.shared_document.downloading')}
      {:else}
        {$_('pages.shared_document.download')}
      {/if}
    </button>
  </div>
{/if}
